<template>
  <div class="settings-section scheduled-activations" test-id="scheduled-activations">
    <voxy-table
      :columns="columns"
      :rows="activations"
      :sortable="false"
      :infinity="true"
      :current-page="currentPage"
      @change:pagesize="onPageSizeChange"
      @change:currentPage="onPageChange"
    >
      <template #action="{ row }">
        <VoxyButton
          v-if="shouldShowCancelButton(row.activation)"
          view="plain"
          size="s"
          @click="showScheduledActivationConfirmationModal(row.id)"
        >
          <div class="scheduled-activations__cancel">Cancel</div>
        </VoxyButton>
        <span v-else class="scheduled-activations__empty-data-placeholder" />
      </template>
    </voxy-table>
    <div class="scheduled-activations__loader" v-if="isFetching">
      <voxy-spinner color="#ffffff" :size="48" :stroke="2"></voxy-spinner>
    </div>
    <success-modal
      v-if="successModalIsOpen"
      :title="messages.success.title"
      :details="messages.success.details"
      @modal:confirm="toggleSuccessModal"
      test-id="scheduled-activations-success-modal"
    ></success-modal>
    <error-modal
      v-if="errorModalIsOpen"
      :title="messages.error.title"
      :details="messages.error.details"
      @modal:close="toggleErrorModal"
      test-id="scheduled-activations-error-modal"
    ></error-modal>
  </div>
</template>

<script>
import VoxyTable from '@/components/voxy/VoxyTable';
import { mapActions, mapMutations, mapState } from 'vuex';
import { VoxyButton, VoxySpinner, SuccessModal, ErrorModal } from '@voxy-team/vue-voxy';
import ConfirmationModal from '@/components/ConfirmationModal';
import { mapWaitingGetters } from 'vue-wait';
import { COLUMNS, MESSAGES } from '../../../store/modules/settings/scheduled-activations/constants';
import isInThePast from '../../../utils/datetime/isInThePast';

const sharedModalParams = {
  classes: 'v--modal v--modal_theme_dark',
  height: 'auto',
};

export default {
  name: 'ScheduledActivations',
  components: {
    VoxyButton,
    VoxySpinner,
    VoxyTable,
    SuccessModal,
    ErrorModal,
  },
  created() {
    this.fetchScheduledActivations();
  },

  data() {
    return {
      successModalIsOpen: false,
      errorModalIsOpen: false,
    };
  },

  computed: {
    ...mapState('scheduledActivations', ['activations', 'currentPage']),
    ...mapWaitingGetters({
      isFetching: 'fetching scheduled activations',
    }),

    columns() {
      return COLUMNS;
    },
    messages() {
      return MESSAGES;
    },
  },
  methods: {
    ...mapActions('scheduledActivations', {
      fetchScheduledActivations: 'FETCH_SCHEDULED_ACTIVATIONS',
      cancelScheduledActivations: 'CANCEL_SCHEDULED_ACTIVATIONS',
    }),
    ...mapMutations('scheduledActivations', { setCurrentPage: 'SET_CURRENT_PAGE' }),
    ...mapMutations('scheduledActivations', { setPageSize: 'SET_PAGE_SIZE' }),

    onPageSizeChange({ pageSize }) {
      this.setPageSize({ pageSize });
      this.fetchScheduledActivations();
    },
    onPageChange({ currentPage }) {
      this.setCurrentPage({ currentPage });
      this.fetchScheduledActivations();
    },
    shouldShowCancelButton(activationDate) {
      return !isInThePast(activationDate);
    },
    showScheduledActivationConfirmationModal(scheduledActivationId) {
      this.$modal.show(
        ConfirmationModal,
        {
          title: 'Cancel Scheduled Activation?',
          text: `Are you sure? You are about to cancel the activation of new learner accounts. This action cannot be undone. You will need to upload the activation CSV file again in the Manage Learners tab.`,
          buttonText: `Yes, I'm sure!`,
          confirmAction: async () => {
            try {
              await this.cancelScheduledActivations(scheduledActivationId);
              this.fetchScheduledActivations();
              this.toggleSuccessModal();
            } catch (error) {
              Rollbar.error('Error while handling Scheduled Activations cancel modal', error);
              this.toggleErrorModal();
            }
          },
        },
        sharedModalParams,
      );
    },
    toggleSuccessModal() {
      this.successModalIsOpen = !this.successModalIsOpen;
    },
    toggleErrorModal() {
      this.errorModalIsOpen = !this.errorModalIsOpen;
    },
  },
};
</script>
<style>
.scheduled-activations {
  position: relative;

  &__loader {
    display: flex;
    justify-content: center;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    padding-top: 3rem;
    background: rgba($night-rider-grey, 0.5);
    align-items: center;
  }

  &__cancel {
    color: $default-blue;
    font-size: 15px;
    font-weight: 700;
    text-transform: uppercase;
  }
}
</style>
